import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faPlus } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import useAcademicCourses, { formatIntakeLabel } from './useAcademicCourses'

function AddCourseComp() {
  const navigate = useNavigate()
  const {
    isAddingCourse,
    isLoading,
    canAddSecondaryCourse,
    courseRecords,
    availableSecondaryCourses,
    availableIntakes,
    availableCampusOptions,
    campusRequiresChoice,
    selectedSecondaryCourse,
    setSelectedSecondaryCourse,
    selectedIntakeId,
    setSelectedIntakeId,
    selectedCampus,
    setSelectedCampus,
    addSecondaryCourse
  } = useAcademicCourses()

  const handleSubmit = async () => {
    const newEntry = await addSecondaryCourse()
    if (newEntry) {
      navigate('/fee-invoices', { state: { secondaryCourse: newEntry } })
    }
  }

  return (
    <div className='addCoursePage'>
      <div className='addCourseHero'>
        <button className='courseBackBtn' onClick={() => navigate('/academic')}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to My Courses
        </button>
        <h4>Add a Course</h4>
        <p>Select an additional course, choose the intake and campus, and we will keep the same payment and activation flow already in place.</p>
      </div>

      <div className='addCourseLayout'>
        <section className='courseAddPanel addCoursePanelStandalone'>
          <div className='courseAddPanelHeader'>
            <h6>Course Selection</h6>
            <p>Use the same secondary-course logic as before, now in its own dedicated page.</p>
          </div>

          {!canAddSecondaryCourse && (
            <p className='courseAddHint'>Adding a new course becomes available once your status is Student.</p>
          )}

          <div className='courseAddControls'>
            <select
              value={selectedSecondaryCourse}
              onChange={(event) => setSelectedSecondaryCourse(event.target.value)}
              disabled={!canAddSecondaryCourse || isLoading}
            >
              <option value=''>Select a course</option>
              {availableSecondaryCourses.map((course) => (
                <option key={course.id} value={course.name}>
                  {course.name}
                </option>
              ))}
            </select>

            <select
              value={selectedIntakeId}
              onChange={(event) => setSelectedIntakeId(event.target.value)}
              disabled={!canAddSecondaryCourse || !selectedSecondaryCourse}
            >
              <option value=''>
                {selectedSecondaryCourse ? 'Select an intake' : 'Choose a course first'}
              </option>
              {availableIntakes.map((intake) => (
                <option key={intake.id} value={intake.id}>
                  {formatIntakeLabel(intake)}
                </option>
              ))}
            </select>

            <select
              value={selectedCampus}
              onChange={(event) => setSelectedCampus(event.target.value)}
              disabled={!selectedIntakeId || availableCampusOptions.length === 0 || (!campusRequiresChoice && availableCampusOptions.length === 1)}
            >
              <option value=''>
                {selectedIntakeId ? 'Select a campus' : 'Choose an intake first'}
              </option>
              {availableCampusOptions.map((campus) => (
                <option key={campus} value={campus}>
                  {campus}
                </option>
              ))}
            </select>

            <button
              type='button'
              onClick={handleSubmit}
              disabled={
                !canAddSecondaryCourse
                || isAddingCourse
                || availableSecondaryCourses.length === 0
                || availableIntakes.length === 0
                || (campusRequiresChoice && !selectedCampus)
              }
            >
              {isAddingCourse ? 'Adding...' : 'Add Course'}
            </button>
          </div>

          {availableSecondaryCourses.length === 0 && (
            <p className='courseAddHint'>You have already added all available courses.</p>
          )}
          {selectedSecondaryCourse && availableIntakes.length === 0 && (
            <p className='courseAddHint'>No intakes are available for this course yet.</p>
          )}
        </section>

        <aside className='addCourseSidebar'>
          <div className='addCourseSidebarCard'>
            <span className='addCourseSidebarEyebrow'>Current courses</span>
            <h6>{courseRecords.length} active course{courseRecords.length === 1 ? '' : 's'}</h6>
            <div className='addCourseChipRow'>
              {courseRecords.map((course) => (
                <span key={course.name} className='addCourseChip'>
                  {course.name}
                </span>
              ))}
            </div>
          </div>

          <div className='addCourseSidebarCard addCourseSidebarCardAccent'>
            <span className='addCourseSidebarEyebrow'>What happens next</span>
            <h6>Invoice flow stays the same</h6>
            <p>After you add the course, we take you straight into the existing invoice and activation flow for that secondary course.</p>
            <div className='addCourseInlineNote'>
              <FontAwesomeIcon icon={faPlus} />
              <span>No backend logic changes, only a cleaner student journey.</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default AddCourseComp
